import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, NO_ERRORS_SCHEMA, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbDatepickerModule, NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { map, ReplaySubject, Subscription, switchMap, tap } from 'rxjs';
import { NotificationService } from '../shared/notification/notification.service';
import { LoadingComponent } from '../shared/spinner/loading/loading.component';
import { OpportunityService } from './opportunity.service';

@Component({
  selector: 'app-opportunity',
  standalone: true,
  imports: [NgSelectModule, NgbDatepickerModule, NgbTypeaheadModule, LoadingComponent, CommonModule, ReactiveFormsModule],
  templateUrl: './opportunity.component.html',

  styleUrls: ['./opportunity.component.scss'],
  schemas: [NO_ERRORS_SCHEMA],
})
export class OpportunityComponent implements OnInit {
  private readonly fireAuth = inject(AngularFireAuth);
  private readonly opportunityService = inject(OpportunityService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);

  private readonly destroyRef = inject(DestroyRef);

  loading = false;

  opportunityForm = new FormGroup({
    Nome_richiedente__c: new FormControl<string>('', Validators.required),
    Email_richiedente__c: new FormControl<string>('', Validators.email),
    Ruolo_richiedente__c: new FormControl<string>(null, Validators.required),
    Nome_Cliente__c: new FormControl<string>('', Validators.required),
    Codice_Fiscale_Cliente__c: new FormControl<string>('', Validators.required),
    ID_Opp_Noovle__c: new FormControl<string>(''),
    ID_SDW__c: new FormControl<string>('', Validators.required),
    Tipologia_Opportunit__c: new FormControl<string>(null, Validators.required),
    Vendor__c: new FormControl<string>({ value: null, disabled: true }, Validators.required),
    Nome_servizio_a_catalogo__c: new FormControl<string>({ value: null, disabled: true }, Validators.required),
    Descrizione_opportunit__c: new FormControl<string>(''),
    Valore_Opportunit__c: new FormControl<number | null>(null, [
      Validators.required,
      Validators.pattern(/^-?(0|[1-9]\d*)?$/),
      Validators.max(99999999999999),
    ]),

    Gara__c: new FormControl<boolean>(false),
    Data_pubblicazione_Gara__c: new FormControl<string>({ value: null, disabled: true }, Validators.required),
    Scadenza_domande_di_chiarimento__c: new FormControl<string>({ value: null, disabled: true }, Validators.required),
    Scadenza_Gara__c: new FormControl<string>({ value: null, disabled: true }, Validators.required),
    URL_Preventivatore__c: new FormControl<string>(''),
  });

  roles$ = this.opportunityService.getList('Ruolo_richiedente__c');
  opportunityType$ = this.opportunityService.getList('Tipologia_Opportunit__c');

  vendorSubject = new ReplaySubject<string>(1);

  vendor$ = this.vendorSubject.pipe(
    map((value, index) => {
      if (index > 0 || !this.activatedRoute.snapshot.queryParams['Vendor__c']) {
        this.opportunityForm.get('Vendor__c').reset();
      }
      if (index > 0 || !this.activatedRoute.snapshot.queryParams['Nome_servizio_a_catalogo__c']) {
        this.opportunityForm.get('Nome_servizio_a_catalogo__c').reset();
      }

      this.opportunityForm.get('Nome_servizio_a_catalogo__c').reset();
      this.opportunityForm.get('Nome_servizio_a_catalogo__c').disable();
      this.loading = true;
      return value;
    }),
    tap(() => { }),
    switchMap((typeValue) => this.opportunityService.getList('Vendor__c', typeValue)),
    tap((_) => {
      this.loading = false;
      this.opportunityForm.get('Vendor__c').enable();
    })
  );

  serviceNameSubject = new ReplaySubject<string>(1);
  serviceName$ = this.serviceNameSubject.pipe(
    map((value, index) => {
      // handle query param injected in url otherwise will reset the value
      if (index > 1 || !this.activatedRoute.snapshot.queryParams['Nome_servizio_a_catalogo__c']) {
        this.opportunityForm.get('Nome_servizio_a_catalogo__c').reset();
      }
      this.loading = true;
      return value;
    }),
    switchMap((vendorValue) => this.opportunityService.getList('Nome_servizio_a_catalogo__c', vendorValue)),
    tap((_) => {
      this.loading = false;
      this.opportunityForm.get('Nome_servizio_a_catalogo__c').enable();
    })
  );

  notificationService = inject(NotificationService);

  submitSubscription: Subscription;

  ngOnInit(): void {
    this.opportunityForm
      .get('Tipologia_Opportunit__c')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => value && this.vendorSubject.next(value));

    this.opportunityForm
      .get('Vendor__c')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => value && this.serviceNameSubject.next(value));

    this.opportunityForm
      .get('Gara__c')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((enabled) => {
        if (enabled) {
          this.enableCompetitionDates();
        } else {
          this.disableCompetitionDates();
        }
      });

    this.fireAuth.user.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) =>
      this.opportunityForm.patchValue({
        ...this.activatedRoute.snapshot.queryParams,
        Email_richiedente__c: user.email,
        Nome_richiedente__c: user.displayName,
      })
    );
  }

  private enableCompetitionDates() {
    this.opportunityForm.get('Data_pubblicazione_Gara__c').enable();
    this.opportunityForm.get('Scadenza_domande_di_chiarimento__c').enable();
    this.opportunityForm.get('Scadenza_Gara__c').enable();
  }
  private disableCompetitionDates() {
    this.opportunityForm.get('Data_pubblicazione_Gara__c').disable();
    this.opportunityForm.get('Scadenza_domande_di_chiarimento__c').disable();
    this.opportunityForm.get('Scadenza_Gara__c').disable();
  }

  formSubmitted() {
    this.opportunityForm.updateValueAndValidity();
    if (this.opportunityForm.invalid) return this.opportunityForm.markAllAsTouched();

    const value = this.opportunityForm.value;
    this.notificationService
      .openWarningUser({
        title: 'Creazione Opportunità',
        message: 'Confermi di voler creare una nuova opportunità',
        nextBtn: 'Conferma',
        cancelBtn: 'Annulla',
      })
      .then(
        (_) =>
        (this.submitSubscription = this.opportunityService.create(value).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.notificationService
              .openNotification({
                title: 'Opportunità creata',
                message: 'Il tuo record è stato creato correttamente su Salesforce',
              })
              .finally(() => this.router.navigate(['/']));
          },
          error: (error) => {
            console.log(error);

            this.notificationService.openNotification({
              title: 'Errore:',
              message: 'Errore durante la creazione del record',
            });
            this.router.navigate(['/']);
          },
        }))
      );
  }
}
